import { getIncidentTypeLabel } from "@/lib/utils/matters/incident-classification";
import { useUser } from "@/components/UserProvider";
import {
  ActionConfirmationModal,
  Badge,
  Button,
  Command,
  CommandItem,
  CommandList,
  Form,
  FormControl,
  FormField,
  FormItem,
  InlineEditableField,
  InlineEditableNumberField,
  Input,
  MultiSelect,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Typography,
} from "@/components/ui";
import { SelectDropdownChevron } from "@/components/ui/shadcn/Select/Select";
import { useOnboardingStepFrameScroll } from "@/components/onboarding/OnboardingStepFrame";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import {
  useCreateMatterDefaultExpense,
  useDeleteMatterDefaultExpense,
  useUpdateMatterDefaultExpense,
} from "@/lib/data/react-query/matters/matter-default-expenses";
import { DefaultMatterExpenseSchema } from "@/lib/data/schemas";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import { MATTER_EXPENSE_CATEGORY_OPTIONS } from "@/lib/data/static/matters/expenseStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { DefaultMatterExpense, IncidentType } from "@/lib/types";
import { ErrorBoundary } from "@sentry/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { isEqual, sortBy, toLower } from "lodash";
import { Check, Trash2 } from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";

const formSchema = DefaultMatterExpenseSchema.pick({
  label: true,
  category: true,
  incidentTypeIds: true,
  cost: true,
});

const defaultFormValues: z.infer<typeof formSchema> = {
  label: "",
  category: "",
  incidentTypeIds: [],
  cost: null,
};

const inlineFieldSchemas = {
  label: DefaultMatterExpenseSchema.shape.label,
  category: DefaultMatterExpenseSchema.shape.category,
  incidentType: DefaultMatterExpenseSchema.shape.incidentTypeIds,
  cost: DefaultMatterExpenseSchema.shape.cost,
};

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatCurrencyLabel = (value: number | null) => {
  if (value === null) return "No cost";
  return currencyFormatter.format(value).replace(/\.00$/, "");
};

type AddMatterExpenseEditorProps = {
  expenses: DefaultMatterExpense[];
  incidentTypes: IncidentType[];
  isReadOnly: boolean;
  onClose: () => void;
};

function AddMatterExpenseEditor({
  expenses,
  incidentTypes,
  isReadOnly,
  onClose,
}: AddMatterExpenseEditorProps) {
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [selectedIncidentTypes, setSelectedIncidentTypes] = useState<string[]>(
    defaultFormValues.incidentTypeIds,
  );
  const createMatterDefaultExpense = useCreateMatterDefaultExpense({
    showToast: false,
  });
  const isFormLoading = createMatterDefaultExpense.isPending;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultFormValues,
    mode: "onSubmit",
  });

  useEffect(() => {
    form.reset(defaultFormValues);
    setSelectedIncidentTypes(defaultFormValues.incidentTypeIds);
  }, [form]);

  const createErrorMessage = useMemo(() => {
    const errors = [
      form.formState.errors.label?.message,
      form.formState.errors.category?.message,
      form.formState.errors.incidentTypeIds?.message,
      form.formState.errors.cost?.message,
    ].filter(Boolean);

    return errors.length > 0 ? String(errors[0]) : null;
  }, [form.formState.errors]);

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    if (
      expenses.some(
        (expense) =>
          toLower(expense.label.trim()) === toLower(data.label.trim()),
      )
    ) {
      form.setError("label", {
        message: "Matter cost label must be unique",
      });
      return;
    }

    createMatterDefaultExpense.mutate({
      defaultMatterExpense: {
        ...data,
      },
    });
    onClose();
    form.reset(defaultFormValues);
    setSelectedIncidentTypes(defaultFormValues.incidentTypeIds);
  };

  return (
    <div>
      <Form {...form}>
        <form
          className="relative w-full"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <fieldset
            disabled={isReadOnly || isFormLoading}
            className="bg-muted/60 flex w-full flex-col gap-2 rounded-xl px-2 py-1.5 lg:flex-row lg:items-center lg:justify-between"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <FormField
                  control={form.control}
                  name="label"
                  render={({ field }) => (
                    <FormItem className="space-y-0">
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="New cost label"
                          className="border-border/40 h-9 w-44 bg-white px-2 py-0.5 text-sm leading-normal focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              {createErrorMessage ? (
                <Typography className="text-destructive text-xs">
                  {createErrorMessage}
                </Typography>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 lg:flex-nowrap lg:justify-end">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem className="space-y-0">
                    <Popover
                      modal
                      open={categoryOpen}
                      onOpenChange={setCategoryOpen}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={categoryOpen}
                          aria-label="Select cost category"
                          disabled={isReadOnly || isFormLoading}
                          className="group border-border/40 h-10 w-56 justify-between gap-2 bg-white px-2 py-0.5 text-xs leading-normal shadow-none focus:ring-0 focus:ring-offset-0"
                        >
                          <span
                            className={
                              field.value ? "truncate" : "text-muted-foreground"
                            }
                          >
                            {field.value ? field.value : "Category"}
                          </span>
                          <SelectDropdownChevron />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="min-w-[200px] p-0"
                        align="start"
                      >
                        <Command>
                          <CommandList>
                            {MATTER_EXPENSE_CATEGORY_OPTIONS.map((option) => (
                              <CommandItem
                                key={option}
                                value={option}
                                onSelect={() => {
                                  field.onChange(option);
                                  setCategoryOpen(false);
                                }}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${field.value === option ? "opacity-100" : "opacity-0"}`}
                                />
                                {option}
                              </CommandItem>
                            ))}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="incidentTypeIds"
                render={({ field }) => (
                  <FormItem className="space-y-0">
                    <FormControl>
                      <MultiSelect
                        selectedValues={selectedIncidentTypes}
                        setSelectedValues={setSelectedIncidentTypes}
                        options={incidentTypes.map((incidentType) => ({
                          label: getIncidentTypeLabel(incidentType),
                          value: incidentType.id,
                        }))}
                        placeholder={
                          incidentTypes.length === 0
                            ? "No incident types"
                            : "Incident types"
                        }
                        disabled={incidentTypes.length === 0}
                        onValueChange={(values) => {
                          setSelectedIncidentTypes(values);
                          field.onChange(values);
                        }}
                        maxCount={1}
                        modalPopover
                        showClearButton={false}
                        className="border-border/40 h-7 w-56 bg-white px-1 py-0.5 text-xs leading-normal shadow-none"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cost"
                render={({ field }) => (
                  <FormItem className="space-y-0">
                    <FormControl>
                      <Input
                        type="number"
                        inputMode="decimal"
                        value={field.value ?? ""}
                        placeholder="Cost"
                        min={0}
                        step={0.01}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          field.onChange(
                            nextValue === "" ? null : Number(nextValue),
                          );
                        }}
                        className="border-border/40 h-9 w-19 bg-white px-2 py-0.5 text-xs leading-normal focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                variant="ghost"
                size="xs"
                disabled={isReadOnly || isFormLoading}
                aria-label="Save new cost"
              >
                <Check />
              </Button>
            </div>
          </fieldset>

          <button type="submit" className="hidden" aria-hidden="true" />
        </form>
      </Form>
    </div>
  );
}

type IncidentTypeSelectorProps = {
  expense: DefaultMatterExpense;
  incidentTypes: IncidentType[];
  canUpdate: boolean;
  onChange: (expense: DefaultMatterExpense, nextValues: string[]) => boolean;
};

function IncidentTypeSelector({
  expense,
  incidentTypes,
  canUpdate,
  onChange,
}: IncidentTypeSelectorProps) {
  const [selectedValues, setSelectedValues] = useState<string[]>(
    expense.incidentTypeIds,
  );

  useEffect(() => {
    setSelectedValues(expense.incidentTypeIds);
  }, [expense.incidentTypeIds]);

  return (
    <MultiSelect
      selectedValues={selectedValues}
      setSelectedValues={setSelectedValues}
      options={incidentTypes.map((incidentType) => ({
        label: getIncidentTypeLabel(incidentType),
        value: incidentType.id,
      }))}
      placeholder="Incident types"
      disabled={!canUpdate || incidentTypes.length === 0}
      onValueChange={(values) => {
        setSelectedValues(values);
        const accepted = onChange(expense, values);
        if (!accepted) {
          setSelectedValues(expense.incidentTypeIds);
        }
      }}
      maxCount={1}
      modalPopover
      showClearButton={false}
      className="border-border/40 h-7 w-56 bg-white px-1 py-0.5 text-xs leading-normal shadow-none"
    />
  );
}

function MatterExpensesListDangerous() {
  const [activeExpenseId, setActiveExpenseId] = useState<string | null>(null);
  const [activeField, setActiveField] = useState<
    "label" | "category" | "incidentType" | "cost" | null
  >(null);
  const [categoryDropdownOpenId, setCategoryDropdownOpenId] = useState<
    string | null
  >(null);
  const [inlineValue, setInlineValue] = useState("");
  const [inlineNumberValue, setInlineNumberValue] = useState<number | null>(
    null,
  );
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [pendingDeleteExpense, setPendingDeleteExpense] =
    useState<DefaultMatterExpense | null>(null);
  const { hasActionPermission } = useUser();
  const { scrollToBottom } = useOnboardingStepFrameScroll();
  const previousExpenseCount = useRef<number | null>(null);

  const {
    data: matterSettingsData,
    isLoading: isMatterSettingsLoading,
    isError: isMatterSettingsError,
  } = useQuery(retrieveMatterSettingsQueryOptions());

  const incidentTypes = useMemo(
    () => matterSettingsData?.data.incidentTypes ?? [],
    [matterSettingsData],
  );
  const expenses = useMemo(
    () =>
      sortBy(matterSettingsData?.data.defaultExpenses ?? [], (expense) =>
        new Date(expense.dateCreated).getTime(),
      ),
    [matterSettingsData],
  );

  useEffect(() => {
    if (previousExpenseCount.current === null) {
      previousExpenseCount.current = expenses.length;
      return;
    }

    if (expenses.length > previousExpenseCount.current) {
      scrollToBottom();
    }

    previousExpenseCount.current = expenses.length;
  }, [expenses.length, scrollToBottom]);

  const canCreate = hasActionPermission(
    ACTION_PERMISSION_KEYS.defaultMatterExpense,
    ACTION_PERMISSION_VALUES.create,
  );
  const canUpdate = hasActionPermission(
    ACTION_PERMISSION_KEYS.defaultMatterExpense,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDelete = hasActionPermission(
    ACTION_PERMISSION_KEYS.defaultMatterExpense,
    ACTION_PERMISSION_VALUES.delete,
  );

  const editMatterDefaultExpense = useUpdateMatterDefaultExpense({
    showToast: false,
  });
  const deleteMatterDefaultExpense = useDeleteMatterDefaultExpense({
    showToast: false,
  });

  const closeEditor = () => {
    setActiveExpenseId(null);
    setActiveField(null);
    setCategoryDropdownOpenId(null);
    setInlineValue("");
    setInlineNumberValue(null);
    setInlineError(null);
  };

  const startInlineEdit = (
    expense: DefaultMatterExpense,
    field: "label" | "cost",
  ) => {
    if (!canUpdate) return;
    setActiveExpenseId(expense.id);
    setActiveField(field);
    if (field === "cost") {
      setInlineNumberValue(expense.cost);
      setInlineValue("");
    } else {
      setInlineValue(expense.label);
      setInlineNumberValue(null);
    }
    setInlineError(null);
  };

  const openCategoryEditor = (expense: DefaultMatterExpense) => {
    if (!canUpdate) return;
    closeEditor();
    setActiveExpenseId(expense.id);
    setActiveField("category");
    setInlineValue(expense.category);
    setCategoryDropdownOpenId(expense.id);
  };

  const submitCategoryEdit = (
    expense: DefaultMatterExpense,
    nextValue: string,
  ) => {
    const validation = inlineFieldSchemas.category.safeParse(nextValue);
    if (!validation.success) {
      setInlineError(validation.error.errors[0]?.message ?? "Invalid value");
      return;
    }

    if (validation.data === expense.category) {
      closeEditor();
      return;
    }

    editMatterDefaultExpense.mutate({
      id: expense.id,
      dateModified: expense.dateModified,
      label: expense.label,
      category: validation.data,
      incidentTypeIds: expense.incidentTypeIds,
      cost: expense.cost,
    });
    closeEditor();
  };

  const submitInlineEdit = (expense: DefaultMatterExpense) => {
    if (!activeField || (activeField !== "label" && activeField !== "cost")) {
      return;
    }
    if (!canUpdate) {
      closeEditor();
      return;
    }

    const rawNextValue =
      activeField === "cost" ? inlineNumberValue : inlineValue;
    const validation = inlineFieldSchemas[activeField].safeParse(rawNextValue);
    if (!validation.success) {
      setInlineError(validation.error.errors[0]?.message ?? "Invalid value");
      return;
    }

    const nextValue = validation.data;
    const currentValue = activeField === "cost" ? expense.cost : expense.label;

    if (nextValue === currentValue) {
      closeEditor();
      return;
    }

    if (
      activeField === "label" &&
      expenses.some(
        (item) =>
          toLower(item.label) === toLower(nextValue as string) &&
          item.id !== expense.id,
      )
    ) {
      setInlineError("Matter cost label must be unique");
      return;
    }

    editMatterDefaultExpense.mutate({
      id: expense.id,
      dateModified: expense.dateModified,
      label: activeField === "label" ? (nextValue as string) : expense.label,
      category: expense.category,
      incidentTypeIds: expense.incidentTypeIds,
      cost:
        activeField === "cost" ? (nextValue as number | null) : expense.cost,
    });
    closeEditor();
  };

  const handleIncidentTypeChange = (
    expense: DefaultMatterExpense,
    nextValues: string[],
  ) => {
    if (!canUpdate) return false;
    setActiveExpenseId(expense.id);
    setActiveField("incidentType");
    const validation = inlineFieldSchemas.incidentType.safeParse(nextValues);
    if (!validation.success) {
      setInlineError(validation.error.errors[0]?.message ?? "Invalid value");
      return false;
    }

    const nextValue = validation.data;
    const currentValue = expense.incidentTypeIds;
    if (nextValue.length === 0) {
      setInlineError("Incident type is required");
      return false;
    }

    if (isEqual(sortBy(nextValue), sortBy(currentValue))) {
      setInlineError(null);
      return true;
    }

    editMatterDefaultExpense.mutate({
      id: expense.id,
      dateModified: expense.dateModified,
      label: expense.label,
      category: expense.category,
      incidentTypeIds: nextValue,
      cost: expense.cost,
    });
    setInlineError(null);
    return true;
  };

  const handleInlineKeyDown = (
    event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    expense: DefaultMatterExpense,
  ) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submitInlineEdit(expense);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      closeEditor();
    }
  };

  return (
    <div className="space-y-5">
      <ActionConfirmationModal
        title={`Are you sure you want to delete this matter cost?`}
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        afterActionFn={async () => {
          if (!pendingDeleteExpense) return;
          await deleteMatterDefaultExpense.mutateAsync({
            id: pendingDeleteExpense.id,
          });
          setDeleteModalOpen(false);
          setPendingDeleteExpense(null);
        }}
        onCancel={() => {
          setPendingDeleteExpense(null);
        }}
      />

      {isMatterSettingsLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full rounded-2xl" />
          <Skeleton className="h-20 w-full rounded-2xl" />
          <Skeleton className="h-20 w-full rounded-2xl" />
        </div>
      ) : isMatterSettingsError ? (
        <Typography variant="subHeading">{serverErrorFallbackText}</Typography>
      ) : expenses.length === 0 ? (
        <div className="space-y-2">
          <div className="border-border bg-muted/40 rounded-2xl border border-dashed p-6 text-center">
            <Typography weight="semibold">No matter costs yet</Typography>
            <Typography className="text-muted-foreground text-sm">
              Add your first default cost to speed up matter setup.
            </Typography>
          </div>
          {canCreate ? (
            <AddMatterExpenseEditor
              expenses={expenses}
              incidentTypes={incidentTypes}
              isReadOnly={!canUpdate}
              onClose={closeEditor}
            />
          ) : null}
        </div>
      ) : (
        <div className="space-y-2">
          {expenses.map((expense) => {
            const isEditingLabel =
              activeExpenseId === expense.id && activeField === "label";
            const isEditingCost =
              activeExpenseId === expense.id && activeField === "cost";
            const categoryLabel = expense.category.trim()
              ? expense.category.trim()
              : "No category";

            return (
              <div
                key={expense.id}
                className="hover:bg-muted/30 focus-visible:ring-primary flex flex-col gap-2 rounded-xl px-2 py-1.5 transition focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden lg:flex-row lg:items-center lg:justify-between"
              >
                <div className="min-w-0 flex-1">
                  <Typography
                    tag="h4"
                    weight="semibold"
                    className="flex items-center gap-2 text-sm"
                  >
                    <span className="text-foreground flex-none">
                      <InlineEditableField
                        isEditing={isEditingLabel}
                        value={inlineValue}
                        displayValue={expense.label}
                        canEdit={canUpdate}
                        ariaLabel="Matter cost label"
                        inputWidthClass="w-auto max-w-full"
                        autoSize
                        matchDisplaySize
                        onStartEdit={() => {
                          startInlineEdit(expense, "label");
                        }}
                        onChange={(value: string) => {
                          setInlineValue(value);
                          if (inlineError) setInlineError(null);
                        }}
                        onSubmit={() => {
                          submitInlineEdit(expense);
                        }}
                        onKeyDown={(
                          event: KeyboardEvent<
                            HTMLInputElement | HTMLTextAreaElement
                          >,
                        ) => {
                          handleInlineKeyDown(event, expense);
                        }}
                      />
                    </span>
                  </Typography>
                  {activeExpenseId === expense.id && inlineError ? (
                    <Typography className="text-destructive text-xs">
                      {inlineError}
                    </Typography>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 lg:flex-nowrap lg:justify-end">
                  <div className="flex flex-wrap items-center gap-2">
                    <Popover
                      modal
                      open={categoryDropdownOpenId === expense.id}
                      onOpenChange={(nextOpen) => {
                        if (nextOpen) {
                          openCategoryEditor(expense);
                          return;
                        }
                        closeEditor();
                      }}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={categoryDropdownOpenId === expense.id}
                          aria-label="Select cost category"
                          disabled={!canUpdate}
                          className="group border-border/40 h-10 w-56 justify-between gap-2 bg-white px-2 py-0.5 text-xs leading-normal shadow-none focus:ring-0 focus:ring-offset-0"
                        >
                          <span className="truncate">{categoryLabel}</span>
                          <SelectDropdownChevron />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="min-w-[200px] p-0"
                        align="start"
                      >
                        <Command>
                          <CommandList>
                            {MATTER_EXPENSE_CATEGORY_OPTIONS.map((option) => (
                              <CommandItem
                                key={option}
                                value={option}
                                onSelect={() => {
                                  if (inlineError) setInlineError(null);
                                  submitCategoryEdit(expense, option);
                                }}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${
                                    expense.category === option
                                      ? "opacity-100"
                                      : "opacity-0"
                                  }`}
                                />
                                {option}
                              </CommandItem>
                            ))}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <IncidentTypeSelector
                      expense={expense}
                      incidentTypes={incidentTypes}
                      canUpdate={canUpdate}
                      onChange={handleIncidentTypeChange}
                    />
                    <Badge variant="secondary" className="text-xs">
                      <span className="mr-1">Cost:</span>
                      <InlineEditableNumberField
                        isEditing={isEditingCost}
                        value={inlineNumberValue}
                        displayValue={formatCurrencyLabel(expense.cost)}
                        canEdit={canUpdate}
                        ariaLabel="Matter cost"
                        inputWidthClass="w-auto max-w-full"
                        autoSize
                        textClassName="text-xs"
                        min={0}
                        step={0.01}
                        onStartEdit={() => {
                          startInlineEdit(expense, "cost");
                        }}
                        onChange={(value: number | null) => {
                          setInlineNumberValue(value);
                          if (inlineError) setInlineError(null);
                        }}
                        onSubmit={() => {
                          submitInlineEdit(expense);
                        }}
                        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                          handleInlineKeyDown(event, expense);
                        }}
                      />
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="xs"
                      disabled={!canDelete}
                      onClick={() => {
                        setPendingDeleteExpense(expense);
                        setDeleteModalOpen(true);
                      }}
                      aria-label={`Delete ${expense.label}`}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
          {canCreate ? (
            <AddMatterExpenseEditor
              expenses={expenses}
              incidentTypes={incidentTypes}
              isReadOnly={!canUpdate}
              onClose={closeEditor}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

export function MatterExpensesList() {
  return (
    <ErrorBoundary
      fallback={
        <Typography variant="subHeading">{serverErrorFallbackText}</Typography>
      }
    >
      <MatterExpensesListDangerous />
    </ErrorBoundary>
  );
}
